import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Stack, BookmarkSimple, Database, Archive, Gavel, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const CARDS = [
  { key: "input", label: "Input Database", icon: Stack, to: "/input", color: "text-slate-300" },
  { key: "editorial", label: "Editorial Folder", icon: BookmarkSimple, to: "/editorial", color: "text-emerald-400" },
  { key: "working", label: "Reference DB", icon: Database, to: "/working", color: "text-blue-400" },
  { key: "archive", label: "Archive", icon: Archive, to: "/archive", color: "text-amber-400" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEditor = user?.role === "editor" || user?.role === "admin";
  const { data: stats = {} } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/entries/stats")).data });
  const { data: recent = [] } = useQuery({ queryKey: ["recent"], queryFn: async () => (await api.get("/entries")).data });

  return (
    <div className="h-full overflow-y-auto">
      <header className="h-16 flex items-center justify-between px-8 border-b border-white/5 sticky top-0 bg-[#0B0E14]/70 backdrop-blur-xl z-10">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Dashboard</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="dashboard-collect-btn" onClick={() => navigate("/collect")} className="gap-2 bg-blue-600 hover:bg-blue-500 rounded-full transition-colors duration-200">
            <Plus size={16} weight="bold" /> Collect Entry
          </Button>
          {isEditor && (
            <Button data-testid="dashboard-review-btn" variant="outline" onClick={() => navigate("/review")} className="gap-2 rounded-full border-white/10 hover:bg-white/5">
              <Gavel size={16} weight="duotone" /> Review Queue
            </Button>
          )}
        </div>
      </header>
      <div className="p-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-blue-400 mb-1">Welcome back</p>
        <h2 className="text-2xl font-bold tracking-tight mb-8">{user?.name}</h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {CARDS.map((c, i) => (
            <button key={c.key} data-testid={`stat-card-${c.key}`} onClick={() => navigate(c.to)}
              style={{ animationDelay: `${i * 60}ms` }}
              className="animate-fade-in-up text-left bg-[#131722] border border-white/5 rounded-lg p-5 hover:bg-[#1C2130] transition-colors duration-200 group">
              <c.icon size={22} weight="duotone" className={c.color} />
              <div className="text-3xl font-bold mt-3 tracking-tight" data-testid={`stat-count-${c.key}`}>{stats[c.key] ?? 0}</div>
              <div className="text-sm text-slate-400 mt-0.5">{c.label}</div>
            </button>
          ))}
        </div>

        <h3 className="text-sm font-semibold tracking-tight mb-3 text-slate-300">Recent Activity</h3>
        <div className="bg-[#131722] border border-white/5 rounded-lg divide-y divide-white/5">
          {recent.slice(0, 8).map((e) => (
            <div key={e.id} data-testid={`recent-row-${e.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-white/5 transition-colors duration-200">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{e.title}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{e.created_by_name} · {e.status}</div>
              </div>
            </div>
          ))}
          {recent.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-500">No entries yet. Start by collecting one.</div>}
        </div>
      </div>
    </div>
  );
}
